import { useCallback, useState } from "react";
import { uploadToPresignedUrl } from "../../../api/tournament/galleryService";

export interface UploadQueueItem {
  /** mediaId (season albums) or photoId (match uploads) returned by initiate. */
  id: string;
  file: File;
  isFreePreview: boolean;
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
}

export interface UploadQueueState {
  id: string;
  fileName: string;
  isFreePreview: boolean;
  status: "uploading" | "processing" | "ready" | "failed";
  error?: string;
}

export interface CompleteResult {
  /** `"ready"` only on an idempotent replay of an already-completed media —
   * a fresh completion always comes back `"processing"` (guide §15/§20). */
  status: "processing" | "ready";
}

/**
 * Runs direct-to-S3 PUTs with limited concurrency, calling `onComplete`
 * (the gallery admin "complete" endpoint) after each successful PUT — the
 * same two-step upload pipeline used by both season albums and match bulk
 * uploads. `complete` itself only queues background image processing and
 * returns `202`, so a successfully completed item lands on `"processing"`
 * here, not `"ready"` — the caller must poll the admin listing
 * (`useProcessingPoller`) to learn the final `"ready"`/`"failed"` outcome
 * and call `setItemStatus` to reflect it.
 */
export function useUploadQueue(
  onComplete: (
    id: string,
    file: File,
    isFreePreview: boolean,
  ) => Promise<CompleteResult>,
  concurrency = 4,
) {
  const [states, setStates] = useState<Record<string, UploadQueueState>>({});

  const patch = useCallback((id: string, patch: Partial<UploadQueueState>) => {
    setStates((prev) => ({ ...prev, [id]: { ...prev[id], ...patch, id } as UploadQueueState }));
  }, []);

  const run = useCallback(
    async (items: UploadQueueItem[]) => {
      setStates((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          next[item.id] = {
            id: item.id,
            fileName: item.file.name,
            isFreePreview: item.isFreePreview,
            status: "uploading",
          };
        });
        return next;
      });

      let cursor = 0;
      const worker = async () => {
        while (cursor < items.length) {
          const item = items[cursor];
          cursor += 1;
          try {
            await uploadToPresignedUrl(item.uploadUrl, item.requiredHeaders, item.file);
            patch(item.id, { status: "processing" });
            const result = await onComplete(item.id, item.file, item.isFreePreview);
            patch(item.id, { status: result.status });
          } catch (err) {
            patch(item.id, {
              status: "failed",
              error: err instanceof Error ? err.message : "Upload failed.",
            });
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(concurrency, items.length) }, worker),
      );
    },
    [onComplete, patch, concurrency],
  );

  /** Called by the caller's `useProcessingPoller` once a queued item
   * settles to its final `ready`/`failed` status. */
  const setItemStatus = useCallback(
    (id: string, status: UploadQueueState["status"], error?: string) => {
      patch(id, { status, error });
    },
    [patch],
  );

  const reset = useCallback(() => setStates({}), []);

  return { states: Object.values(states), run, reset, setItemStatus };
}
